// skipcq: JS-W1028
import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';

import useBeerReviewTags from '../../hooks/useBeerReviewTags';
import { getGlassAssetPathForBeer } from '../../utils/brewhousePortfolio/beerStyleGlassMapping';
import AddBeerDialog from '../beer/AddBeerDialog';
import {
  trackPintPortfolioClickBeerProfileTab,
  trackPintPortfolioClickEditBeer,
  trackPintPortfolioSaveBeerEdit,
} from '../../utils/brewhousePortalAnalytics';
import { getPortfolioBeerListingLabels } from '../../utils/brewhousePortfolio/formatPortfolioBeerListingSummary';
import BeerProfileHeaderCard from './BeerProfileHeaderCard';
import BeerProfileSummaryStatsCard from './BeerProfileSummaryStatsCard';
import PintPortfolioBeerProfileContent from './PintPortfolioBeerProfileContent';
import PintPortfolioBeerReviewsPanel from './PintPortfolioBeerReviewsPanel';
import PintPortfolioBeerWorkspaceTabs, { TAB_PROFILE, TAB_REVIEWS } from './PintPortfolioBeerWorkspaceTabs';

function PintPortfolioBeerProfile({
  beer,
  breweryId,
  breweryName,
  breweryGroupId = null,
  styleMetaMap,
  breweryNameById = {},
}) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_PROFILE);
  const { tagLabelMap } = useBeerReviewTags();

  const styleId = typeof beer.style === 'string' ? beer.style : '';
  const styleMeta = styleId.length > 0 ? styleMetaMap[styleId] : null;
  const styleName =
    styleMeta?.name || (styleId.length > 0 ? styleId : '—');
  const styleType = styleMeta?.type || '';
  const glassPath = getGlassAssetPathForBeer(beer.style, styleMetaMap);
  const descriptionText =
    typeof beer.description === 'string' && beer.description.trim().length > 0
      ? beer.description.trim()
      : null;
  const beerId = typeof beer.id === 'string' ? beer.id : '';

  const listingLocationLabels = useMemo(
    () => getPortfolioBeerListingLabels(beer, breweryNameById),
    [beer, breweryNameById],
  );

  const handleEditOpen = useCallback(() => {
    if (breweryId && beerId.length > 0) {
      try {
        trackPintPortfolioClickEditBeer({ breweryId, beerId });
      } catch {
        // Edit dialog must open even when analytics capture fails.
      }
    }
    setEditDialogOpen(true);
  }, [breweryId, beerId]);

  const handleEditClose = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditSuccess = useCallback(
    (result) => {
      if (breweryId) {
        try {
          trackPintPortfolioSaveBeerEdit({
            breweryId,
            beerId: result?.beerId || beerId,
          });
        } catch {
          // Save success UI must not fail on analytics errors.
        }
      }
      setEditDialogOpen(false);
    },
    [breweryId, beerId],
  );

  const handleTabChange = useCallback(
    (nextTab) => {
      setActiveTab(nextTab);
      if (breweryId && beerId.length > 0) {
        try {
          trackPintPortfolioClickBeerProfileTab({
            breweryId,
            beerId,
            tab: nextTab,
          });
        } catch {
          // Tab switch must succeed even when analytics capture fails.
        }
      }
    },
    [breweryId, beerId],
  );

  const reviewsEnabled = activeTab === TAB_REVIEWS;

  return (
    <>
      <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%' }}>
        <Box sx={{ mb: 2 }}>
          <BeerProfileHeaderCard
            beerName={beer.name || 'Untitled beer'}
            overallRating={beer.ratings?.overall}
          />
        </Box>
        <PintPortfolioBeerWorkspaceTabs
          onTabChange={handleTabChange}
          summaryContent={(
            <BeerProfileSummaryStatsCard
              totalReviews={beer.totalReviews}
              checkinsCount={beer.checkinsCount}
            />
          )}
          profileContent={(
            <PintPortfolioBeerProfileContent
              glassPath={glassPath}
              styleName={styleName}
              styleType={styleType}
              abv={beer.abv}
              descriptionText={descriptionText}
              listingLocationLabels={listingLocationLabels}
              ratings={beer.ratings}
              tags={beer.tags}
              tagLabelMap={tagLabelMap}
              onEditOpen={handleEditOpen}
            />
          )}
          reviewsContent={(
            <PintPortfolioBeerReviewsPanel
              breweryId={breweryId}
              beerId={beerId}
              enabled={reviewsEnabled}
            />
          )}
        />
      </Box>
      <AddBeerDialog
        open={editDialogOpen}
        mode="edit"
        editBeer={beer}
        lockedBreweryId={breweryId || ''}
        lockedBreweryName={breweryName || ''}
        breweryGroupId={breweryGroupId || null}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}

PintPortfolioBeerProfile.propTypes = {
  breweryId: PropTypes.string,
  breweryName: PropTypes.string,
  breweryGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  beer: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    style: PropTypes.string,
    abv: PropTypes.number,
    description: PropTypes.string,
    totalReviews: PropTypes.number,
    checkinsCount: PropTypes.number,
    ratings: PropTypes.shape({
      overall: PropTypes.number,
      appearance: PropTypes.number,
      aroma: PropTypes.number,
      flavor: PropTypes.number,
      mouthfeel: PropTypes.number,
    }),
    tags: PropTypes.objectOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          tag: PropTypes.string,
          count: PropTypes.number,
        }),
      ),
    ),
  }).isRequired,
  styleMetaMap: PropTypes.object.isRequired,
  breweryNameById: PropTypes.objectOf(PropTypes.string),
};

PintPortfolioBeerProfile.defaultProps = {
  breweryId: '',
  breweryName: '',
  breweryGroupId: null,
  breweryNameById: {},
};

export default PintPortfolioBeerProfile;
